
import type { PromotionTypeConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/promotion-type-config'
import type { Promotion } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { ToolsMessages } from '../../models'
import type { PromotionConfigProvider } from '../../providers/interfaces/promotion-config.provider'
import type { PromotionDomainService } from '../../services/interfaces/domain/promotion-domain.service'
import { DomainObjectUtils } from '@empathyco/playboard-core-domain'
import { deepEqual } from '@empathyco/x-utils'
import { computed, defineComponent, ref, watch } from 'vue'
import { EpbAsideForm } from '../../components/shared/epb-aside-form'
import { EpbBadge } from '../../components/shared/epb-badge'
import { EpbTextbox } from '../../components/shared/epb-textbox'
import { CONTAINER } from '../../config'
import { TYPES } from '../../config/inversify.types'
import PromotionType from './promotion-type.component.vue'

export default defineComponent({
  name: 'PromotionSettingAsideForm',
  components: {
    EpbAsideForm,
    EpbBadge,
    EpbTextbox,
    PromotionType,
  },
  props: {
    messages: {
      type: Object as PropType<ToolsMessages['promotions']['aside']>,
      required: true,
    },
    opened: Boolean,
    readOnly: Boolean,
    value: { type: Object as PropType<Promotion['setting']>, required: true },
    types: { type: Array as PropType<Array<PromotionTypeConfig>>, required: true },
  },
  emits: {
    close: () => {},
    submit: (payload: Promotion['setting']) => {},
  },
  setup(props, { emit }) {
    const domainService = CONTAINER.get<PromotionDomainService>(TYPES.PromotionDomainService)
    const { promotionSettingConfigComponent } = CONTAINER.get<PromotionConfigProvider>(
      TYPES.PromotionConfigProvider,
    )

    const formValue = ref<Promotion['setting'] | undefined>(DomainObjectUtils.clone(props.value))

    const isFormEmpty = computed<boolean>(() =>
      deepEqual(domainService.getEmptySetting(), formValue.value),
    )

    const isFormValid = computed<boolean>(() => {
      return domainService.isSettingValid(formValue.value)
    })

    const isClearDisabled = computed<boolean>(() => {
      return props.readOnly || isFormEmpty.value
    })

    const isSubmitDisabled = computed<boolean>(() => {
      return isFormEmpty.value || !isFormValid.value
    })

    const isSubmitVisible = computed<boolean>(() => {
      return !props.readOnly
    })

    const clear = (): void => {
      formValue.value = domainService.getEmptySetting()
    }

    const submit = (): void => {
      if (isSubmitDisabled.value) {
        return
      }
      if (formValue.value) {
        emit('submit', formValue.value)
      }
    }

    const isImageUrlValid = computed<boolean>(() => {
      if (formValue.value.imageUrl === props.value.imageUrl) {
        return true
      }
      return domainService.isImageUrlValid(formValue.value.imageUrl)
    })

    const getTitleErrorMessage = computed(() => {
      const formTitle = formValue.value.title

      if (
        domainService.isTitleValid(formTitle) ||
        (formTitle === props.value.title && formTitle.length < domainService.getTitleMaxLength())
      ) {
        return undefined
      }
      if (!formTitle.length) {
        return props.messages.title.errors.empty
      }
      return props.messages.title.errors.length
    })

    const getImageUrlInfoMessage = computed(() => {
      return isImageUrlValid.value ? props.messages.imageUrl.info : undefined
    })

    const getImageUrlErrorMessage = computed(() => {
      return !isImageUrlValid.value ? props.messages.imageUrl.error : undefined
    })

    const onPromotionTypeChange = (): void => {
      const { position } = domainService.getEmptySetting()

      formValue.value.position = position
    }

    watch(
      () => props.value,
      () => {
        formValue.value = DomainObjectUtils.clone(props.value)
      },
    )

    return {
      baseClass: 'promotions-settings-aside-form',
      clear,
      formValue,
      getImageUrlErrorMessage,
      getImageUrlInfoMessage,
      isClearDisabled,
      isFormEmpty,
      isSubmitDisabled,
      isSubmitVisible,
      getTitleErrorMessage,
      onPromotionTypeChange,
      promotionSettingConfigComponent,
      submit,
      titleMaxLength: domainService.getTitleMaxLength(),
    }
  },
})
